// Story 10.1 — Breadcrumbs derivados de los matches del router. Si bien
// TanStack Router puede calcularlos via `useMatches()`, mantenemos un
// derivado del path para que sea fácilmente testeable sin un router montado.

import { Link, useMatches } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildCrumbs } from './breadcrumbs-utils';

// Re-export para que los tests sigan importando del componente si quisieran.
export { buildCrumbs, type Crumb } from './breadcrumbs-utils';

export function Breadcrumbs({ botName }: { botName?: string }): React.ReactElement {
  const matches = useMatches();
  const last = matches[matches.length - 1];
  const pathname = last?.pathname ?? '/';
  const crumbs = buildCrumbs(pathname, botName);

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm">
      {/* En pantallas angostas solo se ve la página actual. */}
      <ol className="flex min-w-0 items-center gap-1">
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li
              key={`${c.label}-${c.to ?? 'leaf'}`}
              className={cn(
                'items-center gap-1',
                isLast ? 'flex min-w-0' : 'hidden shrink-0 sm:flex',
              )}
            >
              {idx > 0 && (
                <ChevronRight
                  className={cn(
                    'h-3 w-3 shrink-0 text-muted-foreground',
                    isLast && 'hidden sm:block',
                  )}
                  aria-hidden
                />
              )}
              {isLast || !c.to ? (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={cn('truncate font-medium', !isLast && 'text-muted-foreground')}
                >
                  {c.label}
                </span>
              ) : (
                <Link
                  to={c.to}
                  className="text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
